<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { IResponseData } from '~/types'
import {
  type IDiscussionCategoryData,
  type IDiscussionCategory,
  WidgetDiscussionsSelectCategory
} from '~/components/widget/discussions'
import { WidgetDiscussionsListMeiliSearch } from '~/components/widget/discussions/list'
import { WidgetSeoContent, WidgetSeoHeroBanner } from '~/components/widget/seo'
import { WidgetTelegram } from '~/components/widget/telegram'
import { useDiscussionsCategoryStore } from '~/stores/discussions-categories'

definePageMeta({
  path: '/discussions',
  alias: ['/discussions/:category(.*)*']
})

const { t } = useI18n()
const route = useRoute()
const categoryStore = useDiscussionsCategoryStore()

const url = computed(() => useUrlWithParams('/question_categories', {}))
const { data: categoriesData } = await useApi<IResponseData<IDiscussionCategory[]>>(() => url, {
  key: 'discussions-select-category-widget'
})

const categories = computed(() => categoriesData.value?.data || [])
const currentCategoryId = ref<string | null>(null)

if (categoriesData.value?.data) {
  categoryStore.list = categoriesData.value.data
}

const pageInfo = reactive({
  title: '',
  description: ''
})

const discussionsListQueryParams = reactive<Record<string, string>>({
  per_page: '20'
})

const typeParam = Array.isArray(route.params.category)
  ? route.params.category[0]
  : route.params.category

if (typeParam) {
  const { data: categoryDataResponse } = await useApiWith404<
    IResponseData<IDiscussionCategoryData>
  >(`/question_categories/${typeParam}`, {
    key: `discussions-categories-${typeParam}`
  })

  if (categoryDataResponse.value) {
    currentCategoryId.value = categoryDataResponse.value.data.id.toString()
    discussionsListQueryParams['filter[category_id]'] =
      categoryDataResponse.value.data.id.toString()
    pageInfo.title = categoryDataResponse.value.data.title
    pageInfo.description = categoryDataResponse.value.data.description
  }
} else {
  currentCategoryId.value = null
  pageInfo.title = t('page.discussions.category_page.title')
  pageInfo.description = t('page.discussions.category_page.description')
}
const breadcrumbs = computed(() => {
  if (Array.isArray(route.params.category)) {
    return [
      { title: t('page.discussions.category_page.breadcrumbs'), url: '/discussions' },
      { title: pageInfo.title, url: route.path }
    ]
  }
  return [{ title: t('page.discussions.category_page.breadcrumbs'), url: '/discussions' }]
})

useHead(() => ({
  title: route.params.category
    ? `${t('page.discussions.category_page.meta_title_with_category', { category: pageInfo.title })} ${t('seo.suffix.banks_kyrgyzstan')}`
    : `${t('page.discussions.category_page.title')} ${t('seo.suffix.banks_kyrgyzstan')}`
}))
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <Button as-child>
              <NuxtLinkLocale class="w-full" to="/discussions/create">{{
                $t('page.discussions.category_page.ask_question_button')
              }}</NuxtLinkLocale>
            </Button>
            <WidgetDiscussionsSelectCategory :categories="categories" />
            <WidgetTelegram />
          </aside>
        </template>

        <div class="space-y-5" role="main">
          <WidgetSeoHeroBanner
            :heading="
              route.params.category
                ? $t('page.discussions.category_page.heading_with_category', {
                    category: pageInfo.title
                  })
                : $t('page.discussions.category_page.title')
            "
            :text="pageInfo.description"
            illustration="discussions"
            backdrop="blobs"
          />
          <WidgetDiscussionsListMeiliSearch
            :query-params="discussionsListQueryParams"
            :category-id="currentCategoryId"
          />
          <WidgetSeoContent position="bottom" />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
